/**
 * CartaoDoLembrete — passo 18, PR 2 (desenho §5.4). O miolo do aviso na tela quando TODOS os itens do grupo são
 * lembretes que tocaram. Mesma anatomia do `CartaoDaConversa`: corpo clicável = botão esticado ATRÁS do conteúdo, ações
 * e X por cima, estados trocando no lugar.
 *
 *   normal → em voo → "Concluído." / "Adiado para 09:42." (fecha em 2 s)
 *                   ↘ "Não deu." + Tentar de novo (repete só o que faltou)
 *                   ↘ 409 = "já resolvido noutro lugar": o lembrete sai do grupo SEM "Não deu."; se não sobrar
 *                     nenhum, a frase do servidor é o resultado (fecha em 2 s)
 *                   ↘ sobrou lembrete que chegou durante o voo → volta a "normal" com ele (não fecha)
 *   normal → o lembrete foi concluído/adiado/remarcado/cancelado FORA do cartão (bolha, painel, outra aba, push): sai
 *            do grupo na hora e, se não sobrar nenhum, o cartão fecha — sem "Não deu." (ver `lembretesResolvidosFora`)
 *
 * ⚠️ O `ToastAction` do Radix FECHA o aviso ao ser clicado: todo handler faz `preventDefault()`.
 * Com mais de um lembrete, [Concluir] e [Adiar 10 min] valem para TODOS (a regra do aviso agrupado, 03/10).
 * Clicar no corpo abre a nota (`?nota=`) e marca o aviso como lido — NÃO conclui (D4-a: abrir não é ter feito).
 * Sem react-query aqui (o Toaster fica fora do provider nos testes): as ações chegam por `acoes`.
 *
 * ⚠️ O aviso fica na tela até a pessoa agir (D3-a), então o cartão age sobre estado que envelhece:
 *  - o grupo é deduplicado por `lembrete.id` (o mesmo lembrete adiado, tocando de novo, é UM item: o do toque novo);
 *    o `abrir` marca como lidas TODAS as notificações do grupo, também as dos toques antigos;
 *  - `emVoo` é uma ref (como no `CartaoDaConversa`): o 2º clique não pede de novo, mesmo que o estado visual tenha
 *    mudado porque chegou lembrete novo;
 *  - "tratado" = a notificação do item já foi concluída/adiada ou recebeu 409; é por NOTIFICAÇÃO (não por lembrete)
 *    para o toque novo do mesmo lembrete voltar a valer. A resposta de um pedido só encerra os itens que estavam nele:
 *    o que chegou durante o voo continua à espera da pessoa.
 */
import { AlarmClock } from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";

import { ToastAction, ToastClose, ToastDescription, ToastTitle } from "@/components/ui/toast";
import { horaCurta } from "@/lib/lembretes/horas";
import { cn } from "@/lib/utils";
import type { AcoesDoAviso, ItemDeLembrete } from "./AvisoNaTela";
import { quandoDoAviso } from "./auxiliaresDoAviso";
import { inscreverResolvidosFora, resolvidosFora } from "./lembretesResolvidosFora";

const FECHA_DEPOIS_MS = 2000;
/** A frase que o servidor manda no 409 de concluir (`MSG_JA_ENCERRADO`), se a resposta vier sem `detail`. */
const FRASE_DO_CONFLITO = "Esse lembrete já foi concluído ou cancelado.";
type Estado = "normal" | "em_voo" | "concluido" | "adiado" | "resolvido" | "erro";
type Acao = "concluir" | "adiar";
/** Notificação do item → o que aconteceu com o lembrete dela. */
type Tratados = Record<string, Acao | "resolvido">;

interface Props {
  itens: ItemDeLembrete[];
  acoes: AcoesDoAviso;
  aoFechar: () => void;
}

/** Um item por lembrete, o do toque mais novo no fim (o `atual`). */
function unicosPorLembrete(itens: ItemDeLembrete[]): ItemDeLembrete[] {
  const porLembrete = new Map<string, ItemDeLembrete>();
  for (const a of itens) {
    porLembrete.delete(a.lembrete.id);
    porLembrete.set(a.lembrete.id, a);
  }
  return [...porLembrete.values()];
}

/** 409 = o lembrete já foi resolvido noutro lugar (push, painel, outra aba): concluído, cancelado ou fora de "tocou". */
const ehConflito = (erro: unknown) =>
  (erro as { response?: { status?: number } } | null)?.response?.status === 409;

function fraseDoConflito(erro: unknown): string {
  const detalhe = (erro as { response?: { data?: { detail?: unknown } } } | null)?.response?.data
    ?.detail;
  return typeof detalhe === "string" && detalhe.trim() ? detalhe : FRASE_DO_CONFLITO;
}

export function CartaoDoLembrete({ itens, acoes, aoFechar }: Props) {
  const navigate = useNavigate();
  const unicos = unicosPorLembrete(itens);
  const [estado, setEstado] = useState<Estado>("normal");
  const [tratados, setTratados] = useState<Tratados>({});
  const [adiadoPara, setAdiadoPara] = useState("");
  const [frase, setFrase] = useState(FRASE_DO_CONFLITO);
  const [ultimaAcao, setUltimaAcao] = useState<Acao>("concluir");
  // Lembretes que a pessoa resolveu fora deste cartão: não esperam mais ninguém (o [Concluir] daria 409).
  const externos = useSyncExternalStore(inscreverResolvidosFora, resolvidosFora, resolvidosFora);
  // Os que ainda esperam a pessoa (os já tratados e os resolvidos fora saem da tela); sem nenhum, sobra o último só
  // para o texto de fundo.
  const pendentes = unicos.filter((a) => !(a.id in tratados) && !externos.has(a.lembrete.id));
  const atual = pendentes[pendentes.length - 1] ?? unicos[unicos.length - 1];
  const varios = pendentes.length > 1;

  const idDoUltimo = unicos[unicos.length - 1].id;
  const [ultimoVisto, setUltimoVisto] = useState(idDoUltimo);
  // Lembrete (ou toque) novo no grupo com o cartão em "Concluído."/"Não deu."/frase do 409: volta a mostrar os
  // pendentes. Em voo, quem decide é o fim do pedido (ver `pedir`).
  if (ultimoVisto !== idDoUltimo) {
    setUltimoVisto(idDoUltimo);
    if (estado !== "em_voo") setEstado("normal");
  }

  // Refs: o `aoFechar` muda de identidade a cada `update()`, e o resultado de um pedido em voo não pode agir sobre
  // um grupo que mudou depois do clique.
  const emVoo = useRef(false);
  const fechar = useRef(aoFechar);
  const unicosAtuais = useRef(unicos);
  const externosAtuais = useRef(externos);
  useEffect(() => {
    fechar.current = aoFechar;
    unicosAtuais.current = unicos;
    externosAtuais.current = externos;
  });
  // Tudo o que o cartão mostrava foi resolvido fora dele: fecha, sem "Não deu." nem "Concluído." (não foi ele quem
  // fez). Em voo não: o pedido do cartão pode ser justamente o que gerou o evento, e o fim dele mostra o resultado.
  const semPendentes = pendentes.length === 0;
  useEffect(() => {
    if (semPendentes && (estado === "normal" || estado === "erro")) fechar.current();
  }, [semPendentes, estado]);
  useEffect(() => {
    if (estado !== "concluido" && estado !== "adiado" && estado !== "resolvido") return;
    const relogio = setTimeout(() => fechar.current(), FECHA_DEPOIS_MS);
    return () => clearTimeout(relogio);
  }, [estado]);

  const pedir = async (acao: Acao) => {
    const executar = acao === "concluir" ? acoes.concluirLembrete : acoes.adiarLembrete;
    if (!executar || emVoo.current || pendentes.length === 0) return;
    emVoo.current = true;
    setUltimaAcao(acao);
    setEstado("em_voo");
    try {
      const doPedido = pendentes;
      const resultados = await Promise.allSettled(doPedido.map((a) => executar(a.lembrete)));
      const agora: Tratados = { ...tratados };
      let falhou = false;
      for (let i = 0; i < resultados.length; i++) {
        const r = resultados[i];
        if (r.status === "fulfilled") {
          agora[doPedido[i].id] = acao;
          // Sempre a hora que o SERVIDOR devolveu (a do último adiado), também no "Tentar de novo".
          if (acao === "adiar") setAdiadoPara(horaCurta(new Date(r.value.lembrar_em)));
        } else if (ehConflito(r.reason)) {
          agora[doPedido[i].id] = "resolvido";
          setFrase(fraseDoConflito(r.reason));
        } else {
          falhou = true;
        }
      }
      setTratados(agora);
      if (falhou) {
        setEstado("erro");
        return;
      }
      // Chegou lembrete durante o voo: o pedido não era dele, e o cartão segue à espera da pessoa (D3-a).
      if (
        unicosAtuais.current.some(
          (a) => !(a.id in agora) && !externosAtuais.current.has(a.lembrete.id)
        )
      ) {
        setEstado("normal");
        return;
      }
      const deuCerto = Object.values(agora).some((v) => v === acao);
      setEstado(!deuCerto ? "resolvido" : acao === "concluir" ? "concluido" : "adiado");
    } finally {
      emVoo.current = false;
    }
  };

  const abrir = () => {
    // Todas as notificações do grupo, também as dos toques antigos do mesmo lembrete: a pessoa viu o aviso.
    for (const a of itens) acoes.marcarLida(a.id);
    navigate(`/mensagens?nota=${encodeURIComponent(atual.lembrete.message_id)}`);
    aoFechar();
  };

  const mostraCorpo = estado === "normal" || estado === "em_voo";
  const quantos = (acao: Acao) => Object.values(tratados).filter((v) => v === acao).length;
  const titulo =
    estado === "concluido"
      ? quantos("concluir") > 1
        ? "Concluídos."
        : "Concluído."
      : estado === "adiado"
        ? `${quantos("adiar") > 1 ? "Adiados" : "Adiado"} para ${adiadoPara}.`
        : estado === "resolvido"
          ? frase
          : estado === "erro"
            ? "Não deu."
            : "Lembrete";
  const emVooNaTela = estado === "em_voo";

  return (
    <div className="group/aviso relative flex w-full gap-3 p-4 pr-10">
      {mostraCorpo && (
        <button
          type="button"
          aria-label={`Abrir: ${atual.body}`}
          onClick={abrir}
          className="absolute inset-0 z-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      )}
      <span
        aria-hidden="true"
        className="pointer-events-none relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 [&_svg]:h-4 [&_svg]:w-4"
      >
        <AlarmClock />
      </span>
      <div className="pointer-events-none relative min-w-0 flex-1">
        {varios && mostraCorpo && (
          <p className="mb-2 border-b pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {pendentes.length} lembretes
          </p>
        )}
        <ToastTitle
          aria-live="polite"
          className={cn(
            "leading-5",
            // A frase do 409 é longa e é o resultado inteiro: quebra em vez de cortar.
            estado !== "resolvido" && "truncate",
            mostraCorpo && "group-hover/aviso:underline"
          )}
        >
          {titulo}
        </ToastTitle>
        {mostraCorpo &&
          (varios ? (
            <ToastDescription asChild>
              <ul className="leading-5 text-foreground/80">
                {pendentes.slice(-3).map((a) => (
                  <li key={a.id} className="line-clamp-1">
                    {a.body}
                  </li>
                ))}
                {pendentes.length > 3 && (
                  <li className="text-xs text-muted-foreground">e mais {pendentes.length - 3}</li>
                )}
              </ul>
            </ToastDescription>
          ) : (
            <ToastDescription className="line-clamp-2 leading-5 text-foreground/80">
              {atual.body}
            </ToastDescription>
          ))}
        {/* O rótulo ganha a linha dele, inteiro: ao lado das ações sobrava ~50 px a 1366 e ~20 a 390 e ele saía
            cortado ("Lembr…"). Sem flex-wrap (cicatriz do flex-wrap no desktop): são duas linhas, não uma que quebra. */}
        {mostraCorpo && (
          <p className="mt-2 text-xs text-muted-foreground">
            Lembrete · {quandoDoAviso(atual.created_at)}
          </p>
        )}
        <div
          className={cn(
            "flex items-center justify-end gap-2 text-xs text-muted-foreground",
            mostraCorpo ? "mt-1" : "mt-2"
          )}
        >
          <div className="flex shrink-0 items-center gap-1">
            <div className="pointer-events-auto relative z-10 flex items-center gap-1">
              {mostraCorpo && (
                <>
                  <ToastAction
                    altText="Adiar o lembrete 10 minutos"
                    aria-disabled={emVooNaTela}
                    onClick={(e) => {
                      e.preventDefault(); // o Radix fecharia o aviso; aqui o clique troca o conteúdo
                      void pedir("adiar");
                    }}
                    className="h-11 px-3 text-xs sm:h-8"
                  >
                    Adiar 10 min
                  </ToastAction>
                  <ToastAction
                    altText="Concluir o lembrete"
                    aria-disabled={emVooNaTela}
                    onClick={(e) => {
                      e.preventDefault();
                      void pedir("concluir");
                    }}
                    className="h-11 px-3 text-xs sm:h-8"
                  >
                    Concluir
                  </ToastAction>
                </>
              )}
              {estado === "erro" && (
                <ToastAction
                  altText="Tentar de novo"
                  onClick={(e) => {
                    e.preventDefault();
                    void pedir(ultimaAcao);
                  }}
                  className="h-11 px-3 text-xs sm:h-8"
                >
                  Tentar de novo
                </ToastAction>
              )}
            </div>
            {mostraCorpo && (
              <span aria-hidden="true" className="text-xs font-medium text-primary">
                Abrir ›
              </span>
            )}
          </div>
        </div>
      </div>
      <ToastClose aria-label="Fechar aviso" className="z-10 opacity-100 sm:opacity-0" />
    </div>
  );
}
